/**
 * Every drawing family the device editor can pick, in one list.
 *
 * The editor does not decide which picker a device gets: the hub's class
 * metadata does (a setting with `display: 'drawing'` and a `drawing` id). This
 * registry answers "which family is `drawing: 'tank'`?" and nothing else — the
 * six `is…Device` predicates that used to live in `DeviceEditor.vue` are gone.
 */
import { APPLIANCE_VARIANTS } from './appliance';
import { BINARY_SENSOR_VARIANTS } from './binarySensor';
import { COVER_VARIANTS } from './cover';
import { FAN_VARIANTS } from './fan';
import { HEAT_PUMP_VARIANTS } from './heatPump';
import { LIGHT_VARIANTS } from './light';
import { LOCK_VARIANTS } from './lock';
import { PUMP_VARIANTS } from './pump';
import { READING_VARIANTS } from './reading';
import { TANK_VARIANTS } from './tank';
import type { VariantFamily } from './types';

export type { Bilingual, VariantDevice, VariantFamily, VariantOption } from './types';

/** The families, in the order the editor lists them when a class has several. */
export const VARIANT_FAMILIES: VariantFamily[] = [
  COVER_VARIANTS,
  BINARY_SENSOR_VARIANTS,
  READING_VARIANTS,
  APPLIANCE_VARIANTS,
  LIGHT_VARIANTS,
  PUMP_VARIANTS,
  FAN_VARIANTS,
  HEAT_PUMP_VARIANTS,
  TANK_VARIANTS,
  LOCK_VARIANTS,
];

/** The family a hub setting's `drawing` id names, or `null` for an unknown one. */
export function variantByDrawing(drawing: string | null | undefined): VariantFamily | null {
  const id = (drawing ?? '').trim();
  if (!id) return null;
  return VARIANT_FAMILIES.find((family) => family.drawing === id) ?? null;
}

/** The family a setting key belongs to, or `null`. */
export function variantBySettingKey(settingKey: string | null | undefined): VariantFamily | null {
  const key = (settingKey ?? '').trim();
  if (!key) return null;
  return VARIANT_FAMILIES.find((family) => family.settingKey === key) ?? null;
}
